import React, { useState } from "react";
import { useAuth } from "react-oidc-context";
import awsExports from "../../../aws-exports";
import { Box, FormLabel, Grid, Typography } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import FingerprintIcon from "@material-ui/icons/Fingerprint";
import {
  Button,
  CheckboxGroupInput,
  Edit,
  FormDataConsumer,
  maxLength,
  minLength,
  RadioButtonGroupInput,
  regex,
  required,
  SaveButton,
  SimpleForm,
  TextInput,
  Toolbar,
  TopToolbar,
  useEditController,
  useNotify,
  useRedirect,
} from "react-admin";

import { AddressInput } from "../../../components/common/AddressInput";
import { BackButton } from "../../../components/common/BackButton";
import { redirectToPasskeySetup } from "../../../utils/passkeyUtils";
import { ForceTitle } from "../../../components/common/ForceTitle";
import { HelpButton } from "../../../components/common/HelpButton";
import { SendInviteButton } from "../../../components/common/SendInviteButton";
import { SmartTipIcon } from "../../../components/common/SmartTipIcon";

import { getUserTypeAcls, logError } from "../../../utils/appUtils";
import { REGEX_PHONE, UserType } from "../../../utils/constants";
import { usePasskeyCheck } from "../../../hooks/usePasskeyCheck";
import { UserEditHelp } from "./UserHelp";
import { DeepDeleteButton } from "./DeepDeleteButton";
import { UserSuspendButton } from "./UserSuspendButton";
import {
  disableUserOptions,
  getUserTypeDescription,
  getUserTypeOptions,
} from "./userUtils";
import { UserAside } from "./UserAside";

const validateRequired = required();
const validatePhone = [
  required(),
  regex(REGEX_PHONE, "Must be valid Phone No."),
  minLength(10),
  maxLength(20),
];

const useStyles = makeStyles((theme) => ({
  userTypeLabelRow: {
    display: "inline-flex",
    alignItems: "center",
    marginBottom: theme.spacing(0.5),
  },
  userTypeLabel: {
    fontSize: theme.typography.caption.fontSize,
    lineHeight: 1.2,
  },
  hiddenRadioLabel: {
    display: "none",
  },
}));

const UserEditForm = ({ record, ...props }) => {
  const classes = useStyles();
  const [aclList, setAclList] = useState(getUserTypeAcls(record.type));

  const handleUserTypeChange = (value) => {
    const newAclList = getUserTypeAcls(value);
    setAclList(newAclList);
  };

  return (
    <FormDataConsumer>
      {({ formData, ...rest }) => (
        <Grid container spacing={5}>
          <Grid item xs={12} md={8}>
            <Grid container direction="column" spacing={1}>
              <Grid container direction="row" spacing={1}>
                <Grid item xs={12} md={6}>
                  <TextInput
                    source="firstName"
                    label="First Name"
                    variant="outlined"
                    fullWidth
                    validate={validateRequired}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextInput
                    source="lastName"
                    label="Last Name"
                    variant="outlined"
                    fullWidth
                    validate={validateRequired}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextInput
                    source="email"
                    type="email"
                    variant="outlined"
                    fullWidth
                    parse={(v) => (v !== "" ? v.toLowerCase() : undefined)}
                    disabled={true}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextInput
                    source="phone"
                    type="tel"
                    variant="outlined"
                    fullWidth
                    validate={validatePhone}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography
                    gutterBottom
                    variant="overline"
                    color="textSecondary"
                  >
                    Home Address
                  </Typography>
                  <AddressInput
                    source="address"
                    variant="outlined"
                    fullWidth
                    parse={(v) => (v !== "" ? v : undefined)}
                    allValues={formData}
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12} md={4}>
            <Grid container direction="column" spacing={1}>
              <Grid container direction="row" spacing={1}>
                <Grid item xs={12}>
                  <Box className={classes.userTypeLabelRow}>
                    <FormLabel
                      component="span"
                      className={classes.userTypeLabel}
                    >
                      User Type *
                    </FormLabel>
                    <SmartTipIcon label="user-type" inline ml={1} />
                  </Box>
                  <Box display="flex" fullWidth>
                    <RadioButtonGroupInput
                      source="type"
                      label={false}
                      variant="outlined"
                      row={false}
                      style={{ marginTop: 0 }}
                      classes={{ label: classes.hiddenRadioLabel }}
                      choices={getUserTypeOptions(record)}
                      disabled={disableUserOptions(record)}
                      validate={validateRequired}
                      onChange={handleUserTypeChange}
                      helperText={getUserTypeDescription(formData.type)}
                    />
                  </Box>
                </Grid>
                {formData.type && formData.type !== UserType.MANAGER && (
                  <Grid item xs={12}>
                    <CheckboxGroupInput
                      source="acl"
                      label="Access Permissions"
                      row={false}
                      fullWidth
                      choices={aclList}
                      disabled={disableUserOptions(record)}
                    />
                  </Grid>
                )}
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      )}
    </FormDataConsumer>
  );
};

export const UserEdit = (props) => {
  const notify = useNotify();
  const redirect = useRedirect();
  const { record, basePath } = useEditController(props);
  const auth = useAuth();
  debugger;
  const { hasPasskey, isChecking } = usePasskeyCheck(
    auth.user?.access_token,
    auth.isAuthenticated,
    awsExports.aws_project_region,
  );
  const transform = (data) => {
    data.fullName = `${data.firstName} ${data.lastName}`;
    data.email = data.email.toLowerCase();

    // must have - boundedTextField does not transform to upper case
    if (data.address?.state)
      data.address.state = data.address.state.toUpperCase();
    if (data.address?.zipCode)
      data.address.zipCode = data.address.zipCode.toUpperCase();
    delete data.address?.name;

    return data;
  };

  const onSuccess = (data) => {
    debugger;
    notify(`User updated successfully.`);
    redirect("list", basePath);
  };

  const onFailure = (error) => {
    logError("users:UserEdit:onFailure:User Update", error, notify);
  };

  const UserEditActions = ({ basePath, data }) => (
    <TopToolbar>
      <HelpButton>
        <UserEditHelp {...props} />
      </HelpButton>
      <SendInviteButton label="INVITE" title="Resend invitation" data={data} />
      {window.appUser?.id === record?.id && (
        <Button
          label="Passkey"
          onClick={redirectToPasskeySetup}
          disabled={isChecking || hasPasskey}
        >
          <FingerprintIcon />
        </Button>
      )}
      <BackButton basePath={basePath} />
      {false && data && <UserSuspendButton record={data} />}
    </TopToolbar>
  );

  const UserEditToolbar = (props) => (
    <Toolbar style={{ justifyContent: "space-between" }} {...props}>
      <SaveButton
        label="Save"
        disabled={props.pristine}
        transform={transform}
        onSuccess={onSuccess}
        onFailure={onFailure}
      />
      <DeepDeleteButton record={record} />
    </Toolbar>
  );

  return (
    <Edit
      {...props}
      title={<ForceTitle title="Edit User" />}
      mutationMode="pessimistic"
      actions={<UserEditActions />}
      aside={<UserAside {...props} />}
    >
      <SimpleForm
        variant="outlined"
        submitOnEnter={false}
        toolbar={<UserEditToolbar />}
      >
        <UserEditForm record={record} />
      </SimpleForm>
    </Edit>
  );
};
